import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchQuery, graphql, useMutation, useRelayEnvironment } from 'react-relay';
import { useLocation } from 'react-router-dom';
import { hooksWaitForSellerExists_checkSellerExistsQuery } from './__generated__/hooksWaitForSellerExists_checkSellerExistsQuery.graphql';
import { getScroll, pageWrapper, scrollSource } from './helpers';

import { useAppState } from 'src/shared/appState';
import { setLastUsedTool as setLastUsedToolAction } from './actions';

export const useInfiniteScroll = (fn: () => any, hasNext?: boolean, scrollContainer?: HTMLElement | null, isLoading?: boolean) => {
    let { isAtEnd } = getScroll(scrollContainer || pageWrapper);

    const handleScroll = () => {
        const { isAtEnd } = getScroll(scrollContainer || pageWrapper);

        if (isAtEnd && hasNext && !isLoading) {
            fn();
        }
    };

    useEffect(() => {
        (scrollContainer || scrollSource).addEventListener('scroll', handleScroll);
        return () => (scrollContainer || scrollSource).removeEventListener('scroll', handleScroll);
    }, [scrollContainer, fn]);

    useEffect(() => {
        let { isAtEnd } = getScroll(scrollContainer || pageWrapper);

        if (isAtEnd && hasNext && !isLoading) {
            fn();
        }
    }, [isAtEnd, fn]);
};

export const useClickOutside = (ref: React.MutableRefObject<HTMLElement | null>, fn: () => void, refExclude?: React.MutableRefObject<HTMLElement | null>) => {
    useEffect(() => {
        /**
         * Alert if clicked on outside of element
         */
        const handleClickOutside = (event: any) => {
            if (ref && ref.current && !ref.current.contains(event.target) && !refExclude?.current?.contains(event.target)) {
                fn();
            }
        };

        // Bind the event listener
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            // Unbind the event listener on clean up
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [ref]);
};

export const useScrollToAnchor = () => {
    const location = useLocation();
    const lastHash = useRef<string | null>(null);

    useEffect(() => {
        const hash = location.hash.slice(1);
        if (hash && hash !== lastHash.current) {
            lastHash.current = hash;
            const element = document.getElementById(hash);

            if (element) {
                setTimeout(() => {
                    const offset = 150;
                    const elementPosition = element.getBoundingClientRect().top + window.scrollY;
                    const scrollToPosition = elementPosition - offset;

                    window.scrollTo({ top: scrollToPosition, behavior: 'smooth' });
                }, 100);
            }
        }
    }, [location]);
};

export const waitUntilSellerExistsCallback = (timeoutMs = 60000) => {
    const environment = useRelayEnvironment();

    const sleepAsync = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

    return useCallback(async () => {
        const startTime = Date.now();

        while (true) {
            // Check if we've exceeded the timeout
            if (Date.now() - startTime > timeoutMs) {
                console.error('Timeout waiting for seller services to be ready, timeout set to ' + timeoutMs.toString() + 'ms');
                throw new Error(
                    `Timeout (${(
                        timeoutMs / 1000
                    ).toString()}s) exceeded while waiting for services to be ready. Please try again later or contact support if this persists.`
                );
            }
            // Wait for 500ms before checking again
            await sleepAsync(1000);
            try {
                const existsResponse = await fetchQuery<hooksWaitForSellerExists_checkSellerExistsQuery>(
                    environment,
                    graphql`
                        query hooksWaitForSellerExists_checkSellerExistsQuery {
                            existsCurrentSellerOnSubscriptionService
                            existsCurrentSellerOnIntegrationService
                            existsCurrentSellerOnSalesChannelService
                            existsCurrentSellerOnMarketplaceIntegrationService
                            existsCurrentSellerOnMarketingService
                            existsCurrentSellerOnOrderService
                        }
                    `,
                    {}
                ).toPromise();

                // Check if all services exist and return true when they do
                if (
                    existsResponse?.existsCurrentSellerOnSubscriptionService &&
                    existsResponse?.existsCurrentSellerOnIntegrationService &&
                    existsResponse?.existsCurrentSellerOnSalesChannelService &&
                    existsResponse?.existsCurrentSellerOnMarketplaceIntegrationService &&
                    existsResponse?.existsCurrentSellerOnMarketingService &&
                    existsResponse?.existsCurrentSellerOnOrderService
                ) {
                    return;
                }
            } catch (error) {
                console.warn("Some services aren't ready yet", error);
            }
        }
    }, []);
};

export const useSellerExistsCheck = (timeoutMs = 60000) => {
    const [ready, setReady] = useState(false);
    const checkForSellerExists = waitUntilSellerExistsCallback(timeoutMs);
    useEffect(() => {
        checkForSellerExists()
            .then(() => setReady(true))
            .catch(() => setReady(false));
    }, [checkForSellerExists]);
    return ready;
};

const mutation = graphql`
    mutation hooksMutation($tool: Int!) {
        setSellerLastUsedTool(tool: $tool) {
            lastUsedTool
        }
    }
`;

export type EnvType = 'CUSTOMIZER' | 'CONFIGURATOR' | 'CONFIGURATOR_2' | 'COMPOSER' | 'VIEWER' | 'AI_STUDIO';

export const ENV_TO_INT: Record<EnvType, number> = {
    CUSTOMIZER: 1,
    CONFIGURATOR: 2,
    CONFIGURATOR_2: 2,
    // in alcuni punti come il product list, il productEnvironment 2 è composer, quindi ho aggiunto anche questo qui
    COMPOSER: 2,
    VIEWER: 3,
    AI_STUDIO: 4
};

export function useLastUsedTool() {
    const [commit] = useMutation(mutation);
    const { dispatch } = useAppState();

    async function setLastUsedTool(type: EnvType): Promise<void> {
        const tool = ENV_TO_INT[type] ?? 1;
        return new Promise((resolve, reject) => {
            commit({
                variables: { tool },
                onCompleted: (data, errors) => {
                    if (errors && errors.length) {
                        console.error('[useLastUsedTool] GraphQL errors:', errors);
                        return reject(new Error(errors.map((e) => e.message).join(',')));
                    }
                    // aggiorno anche lo stato globale
                    dispatch(setLastUsedToolAction(tool));
                    resolve();
                },
                onError: (err) => {
                    console.error('[useLastUsedTool] Network error:', err);
                    reject(err);
                }
            });
        });
    }

    return { setLastUsedTool };
}
